/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from "react";
import CustomInput from "../../inputs/customInput";
import RoleService from "../../../services/roleService";
import { useTheme } from "@emotion/react";

const AddRoleBox = ({ handleReload, setPopupMsg, setPopupType, setShowPopup }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [formValues, setFormValues] = useState({
        name: '',
        permissions: []
    });

    const [permissions, setPermissions] = useState([]);
    const [addErrors, setAddErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newAddErrors = {};

        if (!formValues.name) {
            newAddErrors.name = t('nameRequired');
        }

        if (formValues.permissions.length === 0) {
            newAddErrors.permissions = t('permissionsRequired');
        }

        setAddErrors(newAddErrors);

        return Object.keys(newAddErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const data = await RoleService.add(formValues);
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    handleReload();
                    setFormValues({ name: '', permissions: [] });
                }

                setAddErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setAddErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setAddErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const fetchPermissions = async () => {
        try {
            const data = await RoleService.fetchPermissions();
            setPermissions(data);
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    const handlePermissions = (permissionId) => {
        setFormValues((prevValues) => {
            const isExist = prevValues.permissions.includes(permissionId);
            const updatedPermissions = isExist
                ? prevValues.permissions.filter((p) => p !== permissionId)
                : [...prevValues.permissions, permissionId];
            return {
                ...prevValues,
                permissions: updatedPermissions
            };
        });
    };

    useEffect(() => {
        fetchPermissions();
    }, []);

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: theme.palette.customGrey.borderInput,
                p: isMobile ? 1 : 4,
            }}
        >
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <Typography id="transition-modal-title" variant="h6" component="h2">
                    {t('addRole')}
                </Typography>
            </Box>
            <CustomInput
                label={t('name')}
                placeholder={t('nameHere')}
                name="name"
                type='text'
                value={formValues.name}
                error={!!addErrors.name}
                helperText={addErrors.name}
                onChange={handleChange}
            />

            <p
                style={{
                    color: theme.palette.text.primary,
                    fontSize: 13,
                    marginTop: 24,
                    marginBottom: 4,
                }}
            >
                {t('choosePermissions')}
            </p>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                    '& .MuiFormControlLabel-label': {
                        fontSize: '16px',
                        color: theme.palette.text.primary,
                    },
                }}
            >
                {permissions.map((permission) => (
                    <FormControlLabel
                        key={permission.id}
                        control={
                            <Checkbox
                                checked={formValues.permissions.includes(permission.id)}
                                onChange={() => handlePermissions(permission.id)}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: isMobile ? 12 : 16 }}>
                                {t(permission.name)}
                            </Typography>
                        }
                    />
                ))}
            </Box>


            {addErrors.permissions && (
                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                    {addErrors.permissions}
                </Typography>
            )}


            {addErrors.general && (
                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                    {addErrors.general}
                </Typography>
            )}

            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                mt: addErrors.general ? 2 : 4
            }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                >
                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <p>{t('add')}</p>
                            <AddIcon fontSize="small" sx={{ mx: 0.4 }} />
                        </Box>
                    }
                </Button>
            </Box>
        </Box>
    );
}

export default AddRoleBox;